/**
 * Bloco de campos SaaS da proposta (PR-M1-01c-iii.a+.b).
 *
 * Usado quando `proposal_type` é `saas` ou `saas_consulting`. Expõe:
 * plano, modo do valor (radio desconto% ou preço custom), campo
 * condicional por modo, trial (só SaaS — consultoria tem duração
 * em meses, não dias). Integrado ao `react-hook-form` via prop `form`.
 *
 * Dropdown de plano consome `public.plans` via `usePlansForPicker`
 * (PR fix/proposal-plan-dropdown, 2026-04-24) — planos novos cadastrados
 * em `/superadmin/billing/plans` aparecem automaticamente. Ao mudar o
 * plano, auto-preenche `trial_days` e `custom_price_brl` com os defaults
 * do plano selecionado.
 */
import type { UseFormReturn } from 'react-hook-form';
import { Label, Select } from '@kmgestao/ui';

import { usePlansForPicker } from '../../../features/plans-admin/index.js';
import { type CreateProposalInput } from '../../../features/proposals/index.js';

export interface ProposalSaasFieldsProps {
  form: UseFormReturn<CreateProposalInput>;
  disabled: boolean;
}

const INPUT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm';

export function ProposalSaasFields({ form, disabled }: ProposalSaasFieldsProps) {
  const { register, setValue, watch, getValues } = form;
  const plansQuery = usePlansForPicker();
  // `watch` genérico não tipa bem com discriminated union — double-cast
  // (TS2352 sem `unknown` intermediário) para as variantes que têm price_mode.
  const priceMode = watch('price_mode' as never) as unknown as 'discount' | 'custom' | undefined;
  const currentPlanKey = watch('plan_key' as never) as unknown as string | undefined;

  const plans = plansQuery.data ?? [];
  // Fallback quando catálogo ainda carrega ou está vazio (ambiente novo):
  // mostra pelo menos uma opção plausível pra não travar o form. Se banco
  // tem planos, sempre vence.
  const options =
    plans.length > 0
      ? plans.map((p) => ({ key: p.key, label: p.name_pt_br }))
      : [{ key: 'pro', label: 'Pro (padrão)' }];

  return (
    <section className="space-y-4 rounded-md border border-border bg-muted/20 p-4">
      <header>
        <h4 className="text-sm font-semibold text-foreground">Plataforma (SaaS)</h4>
        <p className="text-xs text-muted-foreground">
          Plano + condição comercial da assinatura mensal da plataforma.
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="plan_key">Plano</Label>
          <Select
            id="plan_key"
            {...register('plan_key' as never, {
              onChange: (e) => {
                // Ao trocar o plano, pré-preenche trial e preço (quando
                // em modo custom) com os defaults do plano selecionado.
                // Se o usuário já personalizou, preserva — só preenche
                // quando o campo está "virgem" (null ou default).
                const newKey = String(e.target.value);
                const plan = plans.find((p) => p.key === newKey);
                if (!plan) return;

                const currentTrial = getValues('trial_days') as unknown as number | null;
                // Só substitui trial se não foi mexido manualmente (usar
                // `trial_days` do plano quando form ainda tem default 14
                // OU o valor atual coincide com trial do plano anterior).
                if (currentTrial === null || currentTrial === undefined) {
                  setValue('trial_days' as never, plan.trial_days as never);
                }

                const mode = getValues('price_mode' as never) as unknown as 'discount' | 'custom';
                if (mode === 'custom' && plan.monthly_price_brl !== null) {
                  const currentCustom = getValues('custom_price_brl' as never) as unknown as
                    | number
                    | null;
                  if (currentCustom === null || currentCustom === undefined) {
                    setValue('custom_price_brl' as never, plan.monthly_price_brl as never);
                  }
                }
              },
            })}
            disabled={disabled || plansQuery.isLoading}
          >
            {options.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </Select>
          {plansQuery.isLoading ? (
            <p className="text-xs text-muted-foreground">Carregando catálogo…</p>
          ) : plans.length === 0 ? (
            <p className="text-xs text-warning">
              Nenhum plano ativo cadastrado em{' '}
              <a href="/superadmin/billing/plans" className="underline">
                /superadmin/billing/plans
              </a>
              . Cadastre um plano pra continuar.
            </p>
          ) : currentPlanKey && !plans.find((p) => p.key === currentPlanKey) ? (
            <p className="text-xs text-destructive">
              Plano &ldquo;{currentPlanKey}&rdquo; não existe mais no catálogo. Selecione um novo.
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="trial_days">Trial (dias)</Label>
          <input
            id="trial_days"
            type="number"
            min={0}
            max={90}
            inputMode="numeric"
            {...register('trial_days', { valueAsNumber: true })}
            disabled={disabled}
            className={INPUT_CLASS}
          />
        </div>
      </div>

      <fieldset className="space-y-2 rounded-md border border-border p-3">
        <legend className="px-1 text-sm font-medium text-foreground">Modo do valor</legend>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              value="discount"
              {...register('price_mode' as never)}
              disabled={disabled}
              onChange={(e) => {
                register('price_mode' as never).onChange(e);
                setValue('custom_price_brl' as never, null as never);
                if (getValues('discount_percent' as never) === null) {
                  setValue('discount_percent' as never, 0 as never);
                }
              }}
              className="size-4 accent-primary"
            />
            Desconto sobre plano
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              value="custom"
              {...register('price_mode' as never)}
              disabled={disabled}
              onChange={(e) => {
                register('price_mode' as never).onChange(e);
                setValue('discount_percent' as never, null as never);
                // Pré-preenche com preço do plano selecionado quando
                // entrar em modo custom (antes caía em 299 hardcoded).
                if (getValues('custom_price_brl' as never) === null) {
                  const key = getValues('plan_key' as never) as unknown as string | undefined;
                  const plan = key ? plans.find((p) => p.key === key) : null;
                  const seed = plan?.monthly_price_brl ?? 299;
                  setValue('custom_price_brl' as never, seed as never);
                }
              }}
              className="size-4 accent-primary"
            />
            Preço customizado
          </label>
        </div>
        <div className="pt-2">
          {priceMode === 'discount' ? (
            <div className="space-y-1.5">
              <Label htmlFor="discount_percent">Desconto (%)</Label>
              <input
                id="discount_percent"
                type="number"
                min={0}
                max={100}
                step={1}
                inputMode="numeric"
                {...register('discount_percent' as never, { valueAsNumber: true })}
                disabled={disabled}
                className={INPUT_CLASS}
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="custom_price_brl">Preço mensal (R$)</Label>
              <input
                id="custom_price_brl"
                type="number"
                min={1}
                step={0.01}
                inputMode="decimal"
                {...register('custom_price_brl' as never, { valueAsNumber: true })}
                disabled={disabled}
                className={INPUT_CLASS}
              />
            </div>
          )}
        </div>
      </fieldset>
    </section>
  );
}
